import type { InputHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { useId, useState } from 'react';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { documentHyphenation, radii, space, text } from '../../../../styles/onboardingUiTokens';

const Box = styled.span<{ $checked: boolean; $error?: boolean }>`
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  margin-top: 2px;
  border-radius: ${radii.sm};
  border: 2px solid ${({ $checked, $error }) => ($checked ? colors.primary : $error ? colors.delay : colors.medium)};
  background: ${({ $checked }) => ($checked ? colors.primary : colors.white)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  font-size: 14px;
  line-height: 1;
  transition:
    border-color 120ms ease,
    background 120ms ease;

  input:focus-visible + & {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

/**
 * Unboxed follows the Figma `Listitem` style (Small 12px in `colors.medium`, ERS-61 feedback);
 * boxed keeps the denser dark copy that reads correctly inside its bordered card.
 */
const LabelText = styled.div<{ $boxed?: boolean }>`
  font-size: ${({ $boxed }) => ($boxed ? '13px' : text.small.fontSize)};
  line-height: 1.5;
  color: ${({ $boxed }) => ($boxed ? colors.dark : colors.medium)};
  ${documentHyphenation}
`;

const TitleText = styled.div`
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 700;
  color: ${colors.dark};
`;

const TextStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
  padding: 2px 0;
`;

const CheckboxHitArea = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: flex-start;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const Root = styled.div<{ $disabled?: boolean; $boxed?: boolean; $error?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $boxed }) => ($boxed ? space.sm : '5px')};
  min-height: 44px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  ${({ $boxed, $disabled, $error }) =>
    $boxed
      ? `
      padding: ${space.sm};
      min-height: unset;
      border-radius: ${radii.control};
      border: 1px solid ${$error ? colors.delay : getMediumRGBA(0.1)};
      background-color: ${getMediumRGBA(0.04)};
      transition: border-color 160ms ease, background-color 160ms ease;
      ${
        $disabled
          ? ''
          : `
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          border-color: ${getMediumRGBA(0.2)};
          background-color: ${getMediumRGBA(0.08)};
        }
      }
      `
      }
  `
      : ''}
`;

const NativeCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

type CheckboxControlProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'checked' | 'defaultChecked' | 'onChange' | 'type' | 'children'
> & {
  defaultChecked?: boolean;
  checked?: boolean;
  onCheckedChange?: (next: boolean) => void;
};

type CheckboxSharedProps = CheckboxControlProps & {
  /** Optional heading above the label body; stays in the text column, left-aligned with the copy. */
  title?: ReactNode;
  /** Bordered container with padding so the whole surface toggles the checkbox; links stay interactive. */
  boxed?: boolean;
  /** Highlights the box (and, when `boxed`, the card border) while true and still unchecked. */
  error?: boolean;
};

export type CheckboxProps =
  | (CheckboxSharedProps & { label: string; children?: undefined })
  | (CheckboxSharedProps & { children: ReactNode; label?: undefined });

function isActivatableLinkTarget(target: HTMLElement) {
  return Boolean(target.closest('a[href], button, [role="link"]'));
}

export function Checkbox(props: CheckboxProps) {
  const autoId = useId();
  const {
    label,
    children,
    title,
    boxed = false,
    error = false,
    id: idProp,
    disabled,
    defaultChecked,
    checked: checkedProp,
    onCheckedChange,
    ...nativeRest
  } = props as CheckboxSharedProps & { label?: string; children?: ReactNode };

  const id = idProp ?? autoId;
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const labelContent = children !== undefined ? children : label;

  const isControlled = checkedProp !== undefined;
  const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false);
  const checked = isControlled ? Boolean(checkedProp) : internalChecked;

  const setChecked = (next: boolean) => {
    if (disabled) {
      return;
    }
    if (!isControlled) {
      setInternalChecked(next);
    }
    onCheckedChange?.(next);
  };

  const hasTitle = title != null && title !== '';

  const ariaLabelledBy = hasTitle ? `${titleId} ${bodyId}` : children !== undefined ? bodyId : undefined;

  const ariaLabel = ariaLabelledBy === undefined && typeof label === 'string' ? label : undefined;

  const handleRootClick = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }
    const target = event.target as HTMLElement;
    if (isActivatableLinkTarget(target)) {
      return;
    }
    if (target.closest('label')) {
      return;
    }
    setChecked(!checked);
  };

  const showError = error && !checked;

  return (
    <Root $disabled={disabled} $boxed={boxed} $error={showError} onClick={handleRootClick}>
      <CheckboxHitArea htmlFor={id} $disabled={disabled} onClick={(e) => e.stopPropagation()}>
        <NativeCheckbox
          {...nativeRest}
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-invalid={showError || undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          onChange={() => {
            setChecked(!checked);
          }}
        />
        <Box $checked={checked} $error={showError} aria-hidden>
          {checked ? '✓' : ''}
        </Box>
      </CheckboxHitArea>
      <TextStack>
        {hasTitle ? <TitleText id={titleId}>{title}</TitleText> : null}
        <LabelText id={bodyId} $boxed={boxed}>
          {labelContent}
        </LabelText>
      </TextStack>
    </Root>
  );
}
